// src/components/proposal-formats/PostSaleTodosSection.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD } from "@/lib/opportunity/proposalTextSections";
import type { OppTodoTemplateScope, OppTodoTemplateStep } from "@/lib/opportunity/oppTodoTemplateTypes";
import {
  deleteOppTodoTemplateStep,
  listOppTodoTemplateSteps,
  reorderOppTodoTemplateSteps,
  upsertOppTodoTemplateStep,
} from "@/lib/opportunity/oppTodoTemplateClient";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";
import { PostSaleTodoStepRow } from "./PostSaleTodoStepRow";

type Props = {
  productType: string;
};

export function PostSaleTodosSection({ productType }: Props) {
  const isOrgDefault = productType === PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD;
  const scope: OppTodoTemplateScope = isOrgDefault ? "org_default" : "product_type";
  const [steps, setSteps] = useState<OppTodoTemplateStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const r = await listOppTodoTemplateSteps(
      isOrgDefault
        ? { scope: "org_default" }
        : { scope: "product_type", product_type: productType },
    );
    setLoading(false);
    if (!r.ok) {
      setLoadError(r.error);
      setSteps([]);
      return;
    }
    setSteps(r.data.steps);
  }, [isOrgDefault, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const displaySteps = useMemo(
    () => [...steps].sort((a, b) => a.sort_order - b.sort_order),
    [steps],
  );

  function bump() {
    setReloadTick((t) => t + 1);
  }

  async function addStep() {
    const nextOrder =
      displaySteps.length > 0 ? Math.max(...displaySteps.map((s) => s.sort_order)) + 1 : 0;
    const r = await upsertOppTodoTemplateStep({
      scope,
      product_type: isOrgDefault ? null : productType,
      label: "New step",
      sort_order: nextOrder,
      default_due_offset_days: null,
    });
    if (r.ok) bump();
    else setLoadError(r.error);
  }

  async function moveStep(id: string, direction: "up" | "down") {
    const idx = displaySteps.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= displaySteps.length) return;

    const reordered = [...displaySteps];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;

    const r = await reorderOppTodoTemplateSteps({
      scope,
      product_type: isOrgDefault ? null : productType,
      ordered_ids: reordered.map((s) => s.id),
    });
    if (r.ok) bump();
    else setLoadError(r.error);
  }

  const title = isOrgDefault ? "Post-sale todos (org default)" : "Post-sale todos (product extensions)";
  const description = isOrgDefault
    ? "Generic checklist steps copied to every deal when it enters In process."
    : "Extra steps for this product type, appended after the org-default list.";

  return (
    <ProposalFormatCollapsibleSection
      title={title}
      description={description}
      headerRight={
        <button
          type="button"
          onClick={() => void addStep()}
          className="px-2 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800 shrink-0"
        >
          + Add step
        </button>
      }
    >
      {loadError ? (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {loadError}
        </div>
      ) : null}
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : displaySteps.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          No steps yet. Add 2–3 lightweight post-sale checks (payment verified, delivery confirmed, etc.).
        </p>
      ) : (
        <div className="space-y-2">
          {displaySteps.map((step, idx) => (
            <PostSaleTodoStepRow
              key={step.id}
              step={step}
              canMoveUp={idx > 0}
              canMoveDown={idx < displaySteps.length - 1}
              onSave={async (payload) => {
                const r = await upsertOppTodoTemplateStep(payload);
                if (r.ok) bump();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onDelete={async (id) => {
                const r = await deleteOppTodoTemplateStep(id);
                if (r.ok) bump();
                return r.ok ? { ok: true } : { ok: false, error: r.error };
              }}
              onMove={(dir) => void moveStep(step.id, dir)}
            />
          ))}
        </div>
      )}
    </ProposalFormatCollapsibleSection>
  );
}
